import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import batchService from 'services/batch';
import { cancelBatchEvaluation } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, currentAssignmentId, currentAssignment, batch, setBatch, formative, studentSubmissions } = useApp();
    const { auth } = useAuth();
    const history = useHistory();
    const { message } = useSnackbar();
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');

    const [uploadSubmissionDialog, setUploadSubmissionDialog] = useState({ open: false });
    const [editFormativeDialog, setEditFormativeDialog] = useState({ open: false });
    const [viewFormativeDialog, setViewFormativeDialog] = useState({ open: false, submissionId: '' });
    const [canCancelBatch, setCanCancelBatch] = useState(true);

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}`);
    };

    const onClickUpload = () => {
        setUploadSubmissionDialog({ open: true });
    };

    const onClickCancel = () => {
        setDialog({
            open: true,
            message: t('dialog.stop-processing-warning.title', 'Stop Draft Processing?'),
            description: t(
                'dialog.stop-processing-warning.description',
                'Are you sure you want to stop the processing of this draft?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                setCanCancelBatch(false);
                try {
                    await cancelBatchEvaluation({ currentAssignment, batch });
                    setBatch((current) => current.filter(({ id }) => id === batch.id));
                } catch (e) {
                    console.error(e);
                    message.error(t('common:errors.something-went-wrong', 'Something went wrong.'));
                } finally {
                    setCanCancelBatch(true);
                }
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickViewSubmission = (submissionId) => {
        history.push(`/assignments/${currentAssignmentId}/submissions/${submissionId}`);
    };

    const createSubmissions = async (submissions) => {
        const timestamp = new Date().getTime();
        const id = uuid();
        const batch = {
            id,
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.id,
            course_id: course.id,
            assignment_id: currentAssignment.id,
            submissions,
            is_finished: false,
            type: 'formative',
            previous_submission_ids: (studentSubmissions ?? [])
                .sort((a, b) => a.created_on - b.created_on)
                .map((submission) => submission.id),
            formative_snapshot: omit(formative, ['_id']),
        };
        setBatch((current) => [...(current || []), batch]);
        try {
            await batchService.createSubmissions(batch);
        } catch (e) {
            console.error(e);
        }
    };

    const onClickViewFormative = (submissionId) => {
        setViewFormativeDialog({ open: true, submissionId });
    };

    const onClickEditFormative = () => {
        setEditFormativeDialog({ open: true });
    };

    return (
        <PageContext.Provider
            value={{
                currentAssignment,
                uploadSubmissionDialog,
                setUploadSubmissionDialog,
                editFormativeDialog,
                setEditFormativeDialog,
                viewFormativeDialog,
                setViewFormativeDialog,
                canCancelBatch,

                onClickBack,
                onClickUpload,
                onClickCancel,
                onClickViewSubmission,
                createSubmissions,
                onClickViewFormative,
                onClickEditFormative,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
